import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useViewportVisibilityForAnimations } from "~/components/1_atoms/useViewportVisibility";

export interface CarouselStandardProps {
  /**
   * Array of image objects with src and alt text
   */
  images: Array<{
    src: string;
    alt: string;
  }>;
  /**
   * Optional id for component identification
   */
  id?: string;
  /**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * CarouselStandard - A 3-image infinite carousel component with smooth sliding animations
 *
 * Features:
 * - True infinite carousel with seamless transitions (no jumps)
 * - Center image at full scale (1.0), side images at 80% scale (0.8)
 * - 3:4 aspect ratio for all images
 * - Smooth sliding transitions with hardware-accelerated transforms
 * - Continuous image positioning - no glitches or flashes
 * - Touch/swipe support for mobile devices (50px minimum distance)
 * - Infinite loop behavior with seamless circular navigation
 * - Responsive design maintaining 3-image layout on all devices
 * - Viewport visibility animations using useViewportVisibilityForAnimations
 * - Follows Tennis Gourmet design system with tg- prefixes
 * - TypeScript interface for props validation
 * - Accessibility support with proper ARIA labels
 * - Performance optimized with CSS transforms and smooth transitions
 *
 * True Infinite Behavior:
 * - Creates extended array with duplicated images for seamless looping
 * - Uses continuous positioning system to eliminate visual jumps
 * - Smooth transitions in both directions (next/previous)
 * - No teleporting or jarring effects when wrapping around
 * - Maintains consistent visual flow regardless of navigation direction
 *
 * @example
 * ```tsx
 * const carouselImages = [
 *   { src: "/images/restaurant-carousel/01.webp", alt: "Restaurant Image 1" },
 *   { src: "/images/restaurant-carousel/02.webp", alt: "Restaurant Image 2" },
 *   { src: "/images/restaurant-carousel/03.webp", alt: "Restaurant Image 3" },
 * ];
 *
 * <CarouselStandard images={carouselImages} id="restaurant-carousel" />
 * ```
 */
export function CarouselStandard({ images, id, className = "" }: CarouselStandardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { ref, isVisible } = useViewportVisibilityForAnimations();

  // Ensure we have at least 3 images for proper carousel functionality
  if (images.length < 3) {
    console.warn("CarouselStandard requires at least 3 images for optimal display");
  }

  // Create extended array for seamless infinite scrolling
  // We create a long enough array to ensure smooth transitions in both directions
  const extendedImages = useMemo(() => {
    if (images.length === 0) return [];

    // Create multiple copies to ensure seamless infinite scrolling
    // Structure: [...images, ...images, ...images, ...images, ...images] (5 full copies)
    // This provides enough buffer for smooth transitions in both directions
    return [...images, ...images, ...images, ...images, ...images];
  }, [images]);

  // Start in the middle set of images (3rd copy) to allow movement in both directions
  const baseOffset = images.length * 2;

  // Current position in the extended array for rendering
  const extendedCurrentIndex = currentIndex + baseOffset;

  // Calculate scale based on distance from center (0 = center, 1 = adjacent, 2+ = far)
  const getScale = (distance: number) => {
    if (distance === 0) return 1.0; // Center slide - full scale
    if (distance === 1) return 0.8; // Adjacent slides - 80% scale
    return 0.6; // Far slides - 60% scale (mostly hidden)
  };

  // Calculate translateX position based on slide index and current center
  // Uses CSS custom property for responsive spacing
  const getTranslateX = (slideIndex: number, centerIndex: number) => {
    const distance = slideIndex - centerIndex;
    // Use CSS custom property for spacing - this will be responsive via CSS media queries
    return `calc(${distance} * var(--carousel-spacing))`;
  };

  // Calculate z-index based on distance from center
  const getZIndex = (distance: number) => {
    return Math.max(0, 100 - Math.abs(distance));
  };

  // Calculate opacity based on distance (fade out distant slides)
  const getOpacity = (distance: number) => {
    if (Math.abs(distance) <= 1) return 1;
    if (Math.abs(distance) <= 2) return 0.3;
    return 0;
  };

  // Get all slide properties for rendering using extended array
  const getSlideStyles = () => {
    return extendedImages.map((_, index: number) => {
      const distance = index - extendedCurrentIndex;

      return {
        transform: `translateX(${getTranslateX(index, extendedCurrentIndex)}) scale(${getScale(Math.abs(distance))})`,
        zIndex: getZIndex(Math.abs(distance)),
        opacity: getOpacity(Math.abs(distance)),
        isActive: distance === 0,
      };
    });
  };

  const slideStyles = getSlideStyles();

  // Navigation handlers with seamless infinite behavior
  const handleNext = useCallback(() => {
    if (isTransitioning || images.length === 0) return;

    setIsTransitioning(true);

    setCurrentIndex((prevIndex) => prevIndex + 1);

    // Reset transition state and handle position reset after animation completes
    setTimeout(() => {
      setIsTransitioning(false);

      // Reset position invisibly if we've moved too far
      setCurrentIndex((currentIdx) => {
        if (currentIdx >= images.length * 2) {
          return currentIdx - images.length;
        }
        return currentIdx;
      });
    }, 500); // Match CSS transition duration
  }, [isTransitioning, images.length]);

  const handlePrevious = useCallback(() => {
    if (isTransitioning || images.length === 0) return;

    setIsTransitioning(true);

    setCurrentIndex((prevIndex) => prevIndex - 1);

    // Reset transition state and handle position reset after animation completes
    setTimeout(() => {
      setIsTransitioning(false);

      // Reset position invisibly if we've moved too far
      setCurrentIndex((currentIdx) => {
        if (currentIdx <= -images.length) {
          return currentIdx + images.length;
        }
        return currentIdx;
      });
    }, 500); // Match CSS transition duration
  }, [isTransitioning, images.length]);

  // Touch/swipe support
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  return (
    <section
      ref={ref}
      className={`relative w-full xl:px-21 2xl:px-60 ${className}`}
      id={id}
      role="region"
      aria-label="Image carousel"
    >
      <div className="tg-carousel-standard__bg bg-tennis-900 absolute inset-0 z-0 xl:inset-x-0 xl:h-1/3"></div>
      <div className="tg-section__grid">
        <div
          className={`tg-carousel-standard col-span-12 transition-all duration-700 ease-in-out ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="tg-carousel-standard__wrapper">
            {/* Images Container */}
            <div
              className="tg-carousel-standard__images"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              data-testid="carousel-images"
            >
              {extendedImages.map((image, index) => {
                const style = slideStyles[index];

                return (
                  <div
                    key={`${image.src}-${index}`}
                    className={`tg-carousel-standard__image-container ${
                      style.isActive
                        ? "tg-carousel-standard__image-container--active"
                        : "tg-carousel-standard__image-container--inactive"
                    }`}
                    style={{
                      transform: style.transform,
                      zIndex: style.zIndex,
                      opacity: style.opacity,
                    }}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="tg-carousel-standard__image"
                      loading="lazy"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Buttons */}
      <button
        type="button"
        onClick={handlePrevious}
        disabled={isTransitioning}
        className={`tg-carousel-standard__nav tg-carousel-standard__nav--prev ${
          isTransitioning ? "tg-carousel-standard__nav--disabled" : ""
        }`}
        aria-label="Previous image"
      >
        <ChevronLeft size={24} className="md:h-8 md:w-8" />
      </button>

      <button
        type="button"
        onClick={handleNext}
        disabled={isTransitioning}
        className={`tg-carousel-standard__nav tg-carousel-standard__nav--next ${
          isTransitioning ? "tg-carousel-standard__nav--disabled" : ""
        }`}
        aria-label="Next image"
      >
        <ChevronRight size={24} className="md:h-8 md:w-8" />
      </button>
      {/* Dot Pagination */}
      <div className="tg-carousel-standard__dots" data-testid="carousel-dots">
        {images.map((image, index) => {
          // Calculate the normalized current index for dot pagination
          const normalizedIndex =
            ((currentIndex % images.length) + images.length) % images.length;

          return (
            <div
              key={`dot-${image.src}`}
              className={`tg-carousel-standard__dot ${
                index === normalizedIndex ? "tg-carousel-standard__dot--active" : ""
              }`}
            />
          );
        })}
      </div>
    </section>
  );
}
